import clsx from "clsx";
import Image from "next/image";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";

import type { WizardFormValues } from "../onboardingWizardForm";
import Skeleton from "~/components/Skeleton";
import {
	useCourseAccreditationsByProfession,
	useOnboarding,
	useProfessions,
} from "~/hooks";
import useCeuGoals from "~/hooks/useCeuGoals";

interface CeuGoalsFieldsProps {
	// The wizard caps the list height so its footer stays visible; the
	// preferences page scrolls as a whole and opts out.
	scroll?: boolean;
}

const CeuGoalsFields = ({ scroll = true }: CeuGoalsFieldsProps) => {
	const { accreditationsByProfession, isLoading: isLoadingAccreditations } =
		useCourseAccreditationsByProfession("true") as ReturnType<
			typeof useCourseAccreditationsByProfession
		> & { isLoading: boolean };
	const { professions } = useProfessions();
	const { onboarding } = useOnboarding();
	const { ceuGoals: savedGoals, isLoadingCeuGoals } = useCeuGoals();

	const { watch, setValue } = useFormContext<WizardFormValues>();
	const ceuGoals = watch("ceuGoals");

	const professionGroups = useMemo(() => {
		return (accreditationsByProfession ?? [])
			.filter((p) => p.accreditations.some((a) => a.coursesCount))
			.map((p) => ({
				...p,
				accreditations: p.accreditations.filter((a) => a.coursesCount),
			}))
			.filter((p) => p.accreditations.length > 0);
	}, [accreditationsByProfession]);

	const hasSeeded = useRef(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: ceuGoals intentionally omitted — snapshot read guarded by hasSeeded ref
	useEffect(() => {
		if (hasSeeded.current) return;
		if (isLoadingAccreditations || isLoadingCeuGoals) return;
		if (!accreditationsByProfession) return;

		if (Object.keys(ceuGoals).length > 0) {
			hasSeeded.current = true;
			return;
		}

		if (savedGoals?.length) {
			for (const g of savedGoals) {
				setValue(`ceuGoals.${g.accreditationId}`, { selected: true });
			}
		} else {
			const primarySlug = professions?.find(
				(p) => p._id === onboarding?.primaryProfessionId,
			)?.slug;
			const linkedIds =
				accreditationsByProfession
					.find((p) => p.slug === primarySlug)
					?.accreditations.map((a) => a._id) ?? [];
			for (const id of linkedIds) {
				setValue(`ceuGoals.${id}`, { selected: true });
			}
		}

		hasSeeded.current = true;
	}, [
		accreditationsByProfession,
		professions,
		onboarding,
		savedGoals,
		isLoadingAccreditations,
		isLoadingCeuGoals,
		setValue,
	]);

	const toggle = (id: string) => {
		const current = ceuGoals[id];
		setValue(
			`ceuGoals.${id}`,
			{ ...current, selected: !current?.selected },
			{ shouldDirty: true },
		);
	};

	if (isLoadingAccreditations || !accreditationsByProfession) {
		return (
			<div className="grid grid-cols-2 gap-3">
				{Array(6)
					.fill(null)
					.map((_, i) => (
						<Skeleton key={i} className="h-14 rounded-xl" />
					))}
			</div>
		);
	}

	return (
		<div
			className={clsx(
				"flex flex-col gap-5",
				scroll && "max-h-96 overflow-y-auto",
			)}
		>
			{professionGroups
				.sort((a, b) => a.title.localeCompare(b.title))
				.map((profession) => (
					<div key={profession.slug} className="flex flex-col gap-3">
						<p className="font-medium text-slate-600 text-sm dark:text-slate-200">
							{profession.title}
						</p>

						<div className="grid grid-cols-2 gap-3">
							{profession.accreditations
								.sort((a, b) => a.title.localeCompare(b.title))
								.map((acc) => {
									const isSelected = !!ceuGoals[acc._id]?.selected;

									return (
										<button
											key={acc._id}
											type="button"
											onClick={() => toggle(acc._id)}
											className={clsx(
												"flex w-full cursor-pointer items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition",
												isSelected
													? "border-sky-300 bg-sky-50 shadow shadow-slate-200 dark:border-sky-600 dark:bg-sky-500/10 dark:shadow-sky-600/20"
													: "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-slate-600 dark:hover:bg-slate-800",
											)}
										>
											<div className="relative h-8 w-8 shrink-0">
												{acc.thumbnail?.url ? (
													<Image
														src={acc.thumbnail.url}
														alt={acc.abbreviation}
														fill
														className="object-contain"
													/>
												) : (
													<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700">
														<span className="font-bold text-slate-500 text-xs dark:text-slate-300">
															{acc.abbreviation.slice(0, 2)}
														</span>
													</div>
												)}
											</div>
											<div className="-mt-0.5 flex min-w-0 flex-col gap-0.5">
												<span className="font-semibold text-black text-sm dark:text-white">
													{acc.abbreviation}
												</span>
												<span className="truncate text-slate-500 text-xs dark:text-slate-200">
													{acc.title}
												</span>
											</div>
										</button>
									);
								})}
						</div>
					</div>
				))}
		</div>
	);
};

export default CeuGoalsFields;
